const SHOW_MORE_TEXT = "Show More";
const SHOW_LESS_TEXT = "Show Less";
const VISUALLY_TRUNCATED_MESSAGE = "visually clipped text";
const SHOW_MORE_BTN_CLASS = "btn-primary btn-full-width-mobile btn-icon-r icon-plus";
const TRANSITION_DURATION = 300;

export class ShowMore {
    container: HTMLElement;
    trigger: HTMLButtonElement;
    triggerContainer: HTMLElement;
    resizeObserver: ResizeObserver | null = null;
    isTruncated: boolean = true;
    isTransitioning: boolean = false;

    constructor(container: HTMLElement) {
        this.container = container;
        this.trigger = document.createElement('button');
        this.init();
    }

    init() {
        const cssClasses = SHOW_MORE_BTN_CLASS.split(" ");
        cssClasses.forEach(className => {
            this.trigger.classList.add(className);
        });
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.setAttribute('type', 'button');
        let containerId = this.container.id;
        if (!containerId) {
            containerId = `a${crypto.randomUUID()}`;
            this.container.id = containerId;
        }
        this.trigger.setAttribute('aria-controls', containerId);
        this.trigger.id = `${containerId}_t`;
        this.trigger.innerHTML = SHOW_MORE_TEXT;
        this.triggerContainer = document.createElement('p');
        this.triggerContainer.appendChild(this.trigger);
        this.container.insertAdjacentElement('afterend', this.triggerContainer);

        this.addEventListeners();
        this.collapse();
        this.resizeObserver = new ResizeObserver(entries => {
            const trimmedContent = entries[0].target.querySelector(':scope > p:first-child') ?? entries[0].target;
            const hasOverflow = entries[0].contentRect.height < trimmedContent.scrollHeight || !!entries[0].target.querySelector(':scope > p:first-child ~ p');

            if (this.isTruncated) {
                if (hasOverflow) {
                    if (this.triggerContainer.hasAttribute('hidden')) {
                        this.triggerContainer.removeAttribute('hidden');
                    }
                    if (!entries[0].target.hasAttribute('aria-describedby')) {
                        entries[0].target.setAttribute('aria-describedby', this.trigger.id);
                        entries[0].target.setAttribute('aria-description', VISUALLY_TRUNCATED_MESSAGE);
                    }
                }
                else {
                    if (!this.triggerContainer.hasAttribute('hidden') && this.isTruncated) {
                        this.triggerContainer.setAttribute('hidden', '');
                        entries[0].target.removeAttribute('aria-describedby');
                        entries[0].target.removeAttribute('aria-description');
                    }
                }
            }

            if (!this.isTransitioning) {
                const el = entries[0].target as HTMLElement;
                el.style.height = 'auto';
                const h = el.offsetHeight;
                el.style.setProperty(this.isTruncated ? '--truncate-height' : '--truncate-height-expanded', `${h}px`);
                el.style.height = '';
            }
        });
        this.resizeObserver.observe(this.container);
    }

    cleanup = () => {
        this.removeEventListeners();
        this.triggerContainer?.remove();
        this.resizeObserver?.disconnect();
    }

    addEventListeners() {
        this.trigger.addEventListener('click', this.toggleTrigger);
    }

    removeEventListeners() {
        this.trigger.removeEventListener('click', this.toggleTrigger);
    }

    toggleTrigger = (e: MouseEvent) => {
        const trigger = e.currentTarget as HTMLButtonElement;
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
            this.collapse();
        }
        else {
            this.expand();
        }
    }

    expand() {
        this.container.style.setProperty('--truncate-height', `${this.container.offsetHeight}px`);
        this.isTruncated = false;
        this.container.setAttribute('data-truncate', 'will-open');
        this.trigger.setAttribute('aria-expanded', 'true');
        this.container.removeAttribute('aria-description');
        this.trigger.innerHTML = SHOW_LESS_TEXT;
        this.isTransitioning = true;
        requestAnimationFrame(() => {
            this.container.setAttribute('data-truncate', 'false');
            this.container.style.setProperty('--truncate-height-expanded', `${this.container.scrollHeight}px`);
            setTimeout(() => { this.isTransitioning = false; }, TRANSITION_DURATION);
        });
    }

    collapse() {
        this.isTruncated = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        this.container.setAttribute('aria-description', VISUALLY_TRUNCATED_MESSAGE);
        this.trigger.innerHTML = SHOW_MORE_TEXT;
        const wasExpanded = this.container.getAttribute('data-truncate') === 'false';
        if (wasExpanded) {
            this.isTransitioning = true;
            this.container.setAttribute('data-truncate', 'closing');
            setTimeout(() => {
                this.container.setAttribute('data-truncate', 'true');
                this.isTransitioning = false;
            }, TRANSITION_DURATION);
        } else {
            this.container.setAttribute('data-truncate', 'true');
        }
    }
}
